import React, { useEffect, useState, useContext, useRef } from "react";
import { MapPin } from "lucide-react";
import { useConfig } from "../../utils/ConfigContext.jsx";
import { createAxiosInstance } from "../../utils/AxiosInstance.jsx";
import FilterContext from "../../utils/FilterContext";
import { Link, useLocation } from "react-router-dom";

const GroupOverview = () => {
  const { API_BASE_URL } = useConfig();
  const AxiosInstance = createAxiosInstance(API_BASE_URL);

  const location = useLocation();
  const currentPath = location.pathname;

  const [leadModel, setLeadModel] = useState(false);
  const [appointmentModel, setAppointmentModel] = useState(false);
  const [patientModel, setPatientModel] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [dataCategory, setDataCategory] = useState("");
  const [patientModalHeader, setPatientModalHeader] = useState("");
  const [isFullScreenModal, setIsFullScreenModal] = useState(false);
  const [isPatientModalFromTotal, setIsPatientModalFromTotal] = useState(false);
  const [centerOrGroupName, setCenterOrGroupName] = useState("");
  /* ----------------------------------------------------
   * FILTERS
   * -------------------------------------------------- */
  const { filters } = useContext(FilterContext);
  const filtersRef = useRef(filters);

  const currentDate = new Date();
  currentDate.setDate(currentDate.getDate() - 1);
  const previousDay = currentDate.toISOString().split("T")[0];

  const [rawStart, rawEnd] = filters?.dateRange?.split(" - ") ?? [];
  const startDate = rawStart?.trim() || previousDay;
  const endDate = rawEnd?.trim() || previousDay;


  const LeadStatusMap = {
    0: "New",
    1: "Scheduled",
    2: "Follow Up",
    3: "Converted",
    4: "Closed",
    5: "Transferred",
  };

  const TYPE_MAP = {
    1: "SDT",
    2: "SNB",
  };

  const PackageStatusMap = {
    0: 'Not Started',
    1: 'Activated',
    2: 'Completed',
    3: "Cancelled",
    4: "Cancelling"
  }

  const TABLE_CONFIG = {
    package: {
      label: "Package Details",
      key: "package_details",
      typeLabel: "Package Type",
      dateLabel: "Booking Date",
      getType: (item) => TYPE_MAP[item.package_type],
      getDate: (item) => formatedDate(item.tppm_date),
      getLastTreatmentDate: (item) => item.last_treatment_date ? formatedDate(item.last_treatment_date) : '',
      getStatus: (item) => PackageStatusMap[item.status],
      getSessions: (item) => item.sessions,
    },
    treatment: {
      label: "Treatment Details",
      key: "treatment_details",
      typeLabel: "Treatment Type",
      dateLabel: "Treatment Date",
      getType: (item) => TYPE_MAP[item.treatment_type],
      getDate: (item) => formatedDate(item.ttm_date),
      getLastTreatmentDate: (item) => item.last_treatment_date ?? '',
      getStatus: (item) => PackageStatusMap[item.package_status],
      getSessions: (item) => item.total_sessions,
    },
  };

  const mode =
    dataCategory === "Patients Treated" ? "treatment" : "package";

  const config = TABLE_CONFIG[mode];

  const [expandedRows, setExpandedRows] = useState({});

  const toggleRow = (pm_id) => {
    setExpandedRows(prev => ({
      ...prev,
      [pm_id]: !prev[pm_id]
    }));
  };

  /* ----------------------------------------------------
   * STATES
   * -------------------------------------------------- */
  const [groups, setGroups] = useState([]);
  const [centerWidgets, setCenterWidgets] = useState({});
  const [centerLoading, setCenterLoading] = useState({});

  /* ----------------------------------------------------
   * EFFECT
   * -------------------------------------------------- */
  useEffect(() => {
    if (!filters) return;

    if (!filtersRef.current || filtersRef.current !== filters) {
      filtersRef.current = filters;
      fetchCenterGroups();
    }
  }, [filters]);

  /* ----------------------------------------------------
   * API CALLS
   * -------------------------------------------------- */

  // 1️⃣ Fetch center groups
  const fetchCenterGroups = async () => {
    try {
      const res = await AxiosInstance.get("/centergroups");
      const groupData = res.data.data || [];

      setGroups(groupData);

      const loaders = {};
      groupData.forEach(group => {
        group.centers.forEach(center => {
          loaders[center.hm_id] = true;
          fetchCenterWidget(center.hm_id);
        });
      });

      setCenterLoading(loaders);

    } catch (err) {
      console.error("Error fetching center groups", err);
    }
  };

  // 2️⃣ Fetch widget for ONE center
  const fetchCenterWidget = async (centerId) => {
    try {
      const res = await AxiosInstance.post("/dashboard/center_widget_count", {
        hm_id: centerId,
        startdate: startDate,
        enddate: endDate,
      });

      setCenterWidgets(prev => ({
        ...prev,
        [centerId]: res.data.data
      }));

    } catch (err) {
      console.error("Widget error for center:", centerId, err);
    } finally {
      setCenterLoading(prev => ({
        ...prev,
        [centerId]: false
      }));
    }
  };

  /* ----------------------------------------------------
   * RENDER
   * -------------------------------------------------- */

  const fetchModalData = async (centerInput, category) => {
    try {
      setModalData([]);
      setModalLoading(true);
      setDataCategory(category);

      // Support both single center id and multiple (group) center ids
      const centerIds = Array.isArray(centerInput) ? centerInput : [centerInput];

      const payload = {
        centerIds,
        category,
        startdate: startDate,
        enddate: endDate,
      };

      if (category === "Leads") {
        setLeadModel(true);
        const res = await AxiosInstance.post(
          "/dashboard/center_widget_details",
          payload
        );
        const flatLeads = (res?.data?.data ?? []).flatMap(source =>
          (source?.total_leads ?? []).map(lead => ({
            ...lead,
            source_name: source.source_name
          }))
        );

        setModalData(flatLeads);
      }
      else if (category === "Appointments") {
        setAppointmentModel(true);
        const res = await AxiosInstance.post(
          "/dashboard/center_widget_details",
          payload
        );

        setModalData(res?.data?.data ?? []);
      }
      else if (category === "Patients New" || category === "Patients Running" || category === "Patients Treated" || category === "Patients Completed") {
        setPatientModel(true);
        setPatientModalHeader(category);
        setIsPatientModalFromTotal(centerIds.length > 1);
        const res = await AxiosInstance.post(
          "/dashboard/center_widget_details",
          payload
        );
        setModalData(res?.data?.data ?? []);

      }

    } catch (err) {
      console.error("Error loading modal data", err);
    } finally {
      setModalLoading(false);
    }
  };

  const DetailItem = ({ type, sessions, status, dateLabel, date, last_treatment_date = null }) => (
    <div
      className="border rounded px-2 py-1 mb-1 bg-white"
    >
      <span className="fw-semibold">{type}-{sessions} ({status})</span><br />
      <span className="text-muted small">{dateLabel}: {date}</span>
      {last_treatment_date && (
        <>
          <br />
          <span className="text-muted small">Last Treatment: {last_treatment_date}</span>
        </>
      )}
    </div>
  );

  const toggleFullScreenModal = () => {
    setIsFullScreenModal(prev => !prev);
  };

  const getCenterWiseCounts = () => {
    if (!modalData || modalData.length === 0) return {};

    const centerCounts = {};

    // For Leads and Appointments - use center_name or hm_name
    if (dataCategory === "Leads" || dataCategory === "Appointments") {
      modalData.forEach(item => {
        const centerName = dataCategory === "Leads" ? item.center_name : item.hm_name;
        if (centerName) {
          centerCounts[centerName] = (centerCounts[centerName] || 0) + 1;
        }
      });
    }
    // For Patients - use center_name
    else if (dataCategory === "Patients New" || dataCategory === "Patients Running" || dataCategory === "Patients Treated" || dataCategory === "Patients Completed") {
      modalData.forEach(patient => {
        const centerName = patient.center_name;
        if (centerName) {
          centerCounts[centerName] = (centerCounts[centerName] || 0) + 1;
        }
      });
    }

    return centerCounts;
  };

  /** When modal is from Group Total, return one aggregated counts object (no center split). */
  const getTotalPackageCounts = () => {
    const byCenter = getPackageCountsByCenter();
    const total = {
      totalPackageCount: 0,
      sdtBoosterCount: 0,
      sdtFullPackageCount: 0,
      snbBoosterCount: 0,
      snbFullPackageCount: 0,
      totalSnbSessions: 0,
      totalSdtSessions: 0,
      totalSnbBoosterSessions: 0,
      totalSdtBoosterSessions: 0,
    };
    Object.values(byCenter).forEach((counts) => {
      total.totalPackageCount += counts.totalPackageCount;
      total.sdtBoosterCount += counts.sdtBoosterCount;
      total.sdtFullPackageCount += counts.sdtFullPackageCount;
      total.snbBoosterCount += counts.snbBoosterCount;
      total.snbFullPackageCount += counts.snbFullPackageCount;
      total.totalSnbSessions += counts.totalSnbSessions;
      total.totalSdtSessions += counts.totalSdtSessions;
      total.totalSnbBoosterSessions += counts.totalSnbBoosterSessions;
      total.totalSdtBoosterSessions += counts.totalSdtBoosterSessions;
    });
    return total;
  };

  const getPackageCountsByCenter = () => {
    if (!modalData || modalData.length === 0) return {};

    const centerCounts = {};

    // Only process for patient categories
    if (dataCategory === "Patients New" || dataCategory === "Patients Running" || dataCategory === "Patients Treated" || dataCategory === "Patients Completed") {
      modalData.forEach(patient => {
        const centerName = patient.center_name;
        if (!centerName) return;

        // Initialize center if not exists
        if (!centerCounts[centerName]) {
          centerCounts[centerName] = {
            totalPackageCount: 0,
            sdtBoosterCount: 0,
            sdtFullPackageCount: 0,
            snbBoosterCount: 0,
            snbFullPackageCount: 0,
            totalSnbSessions: 0,
            totalSdtSessions: 0,
            totalSnbBoosterSessions: 0,
            totalSdtBoosterSessions: 0
          };
        }

        const items = patient[config.key] || [];

        items.forEach(item => {
          centerCounts[centerName].totalPackageCount += 1;

          const packageType = config.getType(item); // "SDT" or "SNB"
          const sessions = config.getSessions(item); // total sessions

          if (packageType === "SDT") {
            centerCounts[centerName].totalSdtSessions += sessions;
            // If sessions not equal to 20, it's a booster
            if (sessions !== 20) {
              centerCounts[centerName].sdtBoosterCount += 1;
              centerCounts[centerName].totalSdtBoosterSessions += sessions;
            } else {
              centerCounts[centerName].sdtFullPackageCount += 1;
            }
          } else if (packageType === "SNB") {
            centerCounts[centerName].totalSnbSessions += sessions;
            // If sessions not equal to 40, it's a booster
            if (sessions !== 40) {
              centerCounts[centerName].snbBoosterCount += 1;
              centerCounts[centerName].totalSnbBoosterSessions += sessions;
            } else {
              centerCounts[centerName].snbFullPackageCount += 1;
            }
          }
        });
      });
    }

    return centerCounts;
  };

  const downloadCsv = () => {
    if (!modalData || modalData.length === 0) {
      alert("No data to download");
      return;
    }

    let csvContent = "";
    let fileName = "data.csv";

    // Handle Leads Modal
    if (dataCategory === "Leads") {
      fileName = "leads.csv";
      const headers = ["#", "Lead Name", "Phone Number", "Age", "Center Name", "Primary Source", "Status"];
      csvContent = headers.join(",") + "\n";

      modalData.forEach((lead, index) => {
        const source = lead.source_name === "Transfer from Other Center"
          ? `Transferred From ${lead.transferred_from}`
          : lead.source_name;
        const row = [
          index + 1,
          `"${lead.name || ""}"`,
          `"${lead.phone || ""}"`,
          `"${lead.dob ? getAge(lead.dob) : '' || ""}"`,
          `"${lead.center_name || ""}"`,
          `"${source || ""}"`,
          LeadStatusMap[lead.status] || "Unknown"
        ];
        csvContent += row.join(",") + "\n";
      });
    }
    // Handle Appointments Modal
    else if (dataCategory === "Appointments") {
      fileName = "appointments.csv";
      const headers = ["#", "Patient Name", "Patient ID", "Age", "Center Name", "Dr. Name", "Date"];
      csvContent = headers.join(",") + "\n";

      modalData.forEach((appointment, index) => {
        const row = [
          index + 1,
          `"${appointment.pm_name || ""}"`,
          `"${appointment.patient_unique_id || ""}"`,
          `"${appointment.pm_dob ? getAge(appointment.pm_dob) : '' || ""}"`,
          `"${appointment.hm_name || ""}"`,
          `"${appointment.doctor_name || "N/A"}"`,
          `"${appointment.pam_app_date || "Unknown"}"`
        ];
        csvContent += row.join(",") + "\n";
      });
    }
    // Handle Patient Modal
    else if (dataCategory === "Patients New" || dataCategory === "Patients Running" || dataCategory === "Patients Treated" || dataCategory === "Patients Completed") {
      fileName = `patients-${dataCategory.toLowerCase().replace(/ /g, "-")}.csv`;

      // For Patients Treated, include SNB and SDT treatment columns
      if (dataCategory === "Patients Treated") {
        const headers = ["#", "Patient Name", "PUID", "Age", "Phone", "Center Name", "SNB Treatments", "SDT Treatments", "Type", "Sessions", "Status", "Date"];
        csvContent = headers.join(",") + "\n";

        modalData.forEach((patient, index) => {
          const items = patient[config.key] || [];

          // Count SNB and SDT treatments
          let snbCount = 0;
          let sdtCount = 0;
          items.forEach(item => {
            const itemType = config.getType(item);
            if (itemType === "SNB") {
              snbCount++;
            } else if (itemType === "SDT") {
              sdtCount++;
            }
          });

          if (items.length === 0) {
            const row = [
              index + 1,
              `"${patient.patient_name || ""}"`,
              `"${patient.patient_unique_id || "N/A"}"`,
              `"${patient.pm_dob ? getAge(patient.pm_dob) : '' || "N/A"}"`,
              `"${patient.phone || "N/A"}"`,
              `"${patient.center_name || ""}"`,
              0,
              0,
              "", "", "", ""
            ];
            csvContent += row.join(",") + "\n";
          } else {
            const firstItem = items[0];
            const row = [
              index + 1,
              `"${patient.patient_name || ""}"`,
              `"${patient.patient_unique_id || "N/A"}"`,
              `"${patient.pm_dob ? getAge(patient.pm_dob) : '' || "N/A"}"`,
              `"${patient.phone || "N/A"}"`,
              `"${patient.center_name || ""}"`,
              snbCount,
              sdtCount,
              config.getType(firstItem),
              config.getSessions(firstItem),
              config.getStatus(firstItem),
              config.getDate(firstItem)
            ];
            csvContent += row.join(",") + "\n";

            // Additional rows for remaining items
            for (let i = 1; i < items.length; i++) {
              const item = items[i];
              const additionalRow = [
                "", "", "", "", "", "", "", "",
                config.getType(item),
                config.getSessions(item),
                config.getStatus(item),
                config.getDate(item)
              ];
              csvContent += additionalRow.join(",") + "\n";
            }
          }
        });
      } else {
        const headers = ["#", "Patient Name", "PUID", "Age", "Phone", "Center Name", "Type", "Sessions", "Status", "Date"];
        csvContent = headers.join(",") + "\n";

        modalData.forEach((patient, index) => {
          const items = patient[config.key] || [];

          if (items.length === 0) {
            const row = [
              index + 1,
              `"${patient.patient_name || ""}"`,
              `"${patient.patient_unique_id || "N/A"}"`,
              `"${patient.pm_dob ? getAge(patient.pm_dob) : '' || "N/A"}"`,
              `"${patient.phone || "N/A"}"`,
              `"${patient.center_name || ""}"`,
              "", "", "", ""
            ];
            csvContent += row.join(",") + "\n";
          } else {
            const firstItem = items[0];
            const row = [
              index + 1,
              `"${patient.patient_name || ""}"`,
              `"${patient.patient_unique_id || "N/A"}"`,
              `"${patient.pm_dob ? getAge(patient.pm_dob) : '' || "N/A"}"`,
              `"${patient.phone || "N/A"}"`,
              `"${patient.center_name || ""}"`,
              config.getType(firstItem),
              config.getSessions(firstItem),
              config.getStatus(firstItem),
              config.getDate(firstItem)
            ];
            csvContent += row.join(",") + "\n";

            // Additional rows for remaining items
            for (let i = 1; i < items.length; i++) {
              const item = items[i];
              const additionalRow = [
                "", "", "", "", "", "",
                config.getType(item),
                config.getSessions(item),
                config.getStatus(item),
                config.getDate(item)
              ];
              csvContent += additionalRow.join(",") + "\n";
            }
          }
        });
      }
    }

    // Create and trigger download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute("download", fileName);
    link.style.visibility = "hidden";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const printData = () => {
    if (!modalData || modalData.length === 0) {
      alert("No data to print");
      return;
    }

    const printTitle =
      dataCategory === "Patients New" || dataCategory === "Patients Running" ||
        dataCategory === "Patients Treated" || dataCategory === "Patients Completed"
        ? patientModalHeader
        : dataCategory;

    let tableHeaders = "";
    let tableRows = "";

    if (dataCategory === "Leads") {
      tableHeaders = "<tr><th>#</th><th>Lead Name</th><th>Phone Number</th><th>Age</th><th>Center Name</th><th>Primary Source</th><th>Status</th></tr>";
      modalData.forEach((lead, index) => {
        const source = lead.source_name === "Transfer from Other Center"
          ? `Transferred From ${lead.transferred_from}`
          : (lead.source_name || "");
        tableRows += `<tr>
          <td>${index + 1}</td>
          <td>${escapeHtml(lead.name || "")}</td>
          <td>${escapeHtml(lead.phone || "")}</td>
          <td>${lead.dob ? getAge(lead.dob) : ""}</td>
          <td>${escapeHtml(lead.center_name || "")}</td>
          <td>${escapeHtml(source)}</td>
          <td>${LeadStatusMap[lead.status] || "Unknown"}</td>
        </tr>`;
      });
    } else if (dataCategory === "Appointments") {
      tableHeaders = "<tr><th>#</th><th>Patient Name</th><th>Patient ID</th><th>Age</th><th>Center Name</th><th>Dr. Name</th><th>Date</th></tr>";
      modalData.forEach((appointment, index) => {
        tableRows += `<tr>
          <td>${index + 1}</td>
          <td>${escapeHtml(appointment.pm_name || "")}</td>
          <td>${escapeHtml(appointment.patient_unique_id || "")}</td>
          <td>${appointment.pm_dob ? getAge(appointment.pm_dob) : ""}</td>
          <td>${escapeHtml(appointment.hm_name || "")}</td>
          <td>${escapeHtml(appointment.doctor_name || "N/A")}</td>
          <td>${escapeHtml(appointment.pam_app_date || "Unknown")}</td>
        </tr>`;
      });
    } else if (dataCategory === "Patients New" || dataCategory === "Patients Running" || dataCategory === "Patients Treated" || dataCategory === "Patients Completed") {
      // For Patients Treated, include SNB and SDT treatment columns
      if (dataCategory === "Patients Treated") {
        tableHeaders = `<tr><th>#</th><th>Patient Name</th><th>PUID</th><th>Age</th><th>Phone</th><th>Center Name</th><th class="text-center">SNB Treatments</th><th class="text-center">SDT Treatments</th><th colSpan="2">${config.label}</th></tr>`;
        modalData.forEach((patient, index) => {
          const items = patient[config.key] || [];
          const name = escapeHtml(patient.patient_name || "");
          const puid = escapeHtml(patient.patient_unique_id || "N/A");
          const age = patient.pm_dob ? getAge(patient.pm_dob) : "N/A";
          const phone = escapeHtml(patient.phone || "N/A");
          const center = escapeHtml(patient.center_name || "");

          // Count SNB and SDT treatments
          let snbCount = 0;
          let sdtCount = 0;
          items.forEach(item => {
            const itemType = config.getType(item); // "SNB" or "SDT"
            if (itemType === "SNB") {
              snbCount++;
            } else if (itemType === "SDT") {
              sdtCount++;
            }
          });

          if (items.length === 0) {
            tableRows += `<tr><td>${index + 1}</td><td>${name}</td><td>${puid}</td><td>${age}</td><td>${phone}</td><td>${center}</td><td class="text-center">0</td><td class="text-center">0</td><td colspan="2">—</td></tr>`;
          } else {
            const first = items[0];
            const detail1 = `${config.getType(first)}-${config.getSessions(first)} (${config.getStatus(first)}) ${config.dateLabel}: ${config.getDate(first)}`;
            tableRows += `<tr><td>${index + 1}</td><td>${name}</td><td>${puid}</td><td>${age}</td><td>${phone}</td><td>${center}</td><td class="text-center">${snbCount}</td><td class="text-center">${sdtCount}</td><td colspan="2">${escapeHtml(detail1)}</td></tr>`;
            for (let i = 1; i < items.length; i++) {
              const item = items[i];
              const detail = `${config.getType(item)}-${config.getSessions(item)} (${config.getStatus(item)}) ${config.dateLabel}: ${config.getDate(item)}`;
              tableRows += `<tr><td></td><td></td><td></td><td></td><td></td><td></td><td></td><td></td><td colspan="2">${escapeHtml(detail)}</td></tr>`;
            }
          }
        });
      } else {
        tableHeaders = `<tr><th>#</th><th>Patient Name</th><th>PUID</th><th>Age</th><th>Phone</th><th>Center Name</th><th colSpan="2">${config.label}</th></tr>`;
        modalData.forEach((patient, index) => {
          const items = patient[config.key] || [];
          const name = escapeHtml(patient.patient_name || "");
          const puid = escapeHtml(patient.patient_unique_id || "N/A");
          const age = patient.pm_dob ? getAge(patient.pm_dob) : "N/A";
          const phone = escapeHtml(patient.phone || "N/A");
          const center = escapeHtml(patient.center_name || "");
          if (items.length === 0) {
            tableRows += `<tr><td>${index + 1}</td><td>${name}</td><td>${puid}</td><td>${age}</td><td>${phone}</td><td>${center}</td><td colspan="2">—</td></tr>`;
          } else {
            const first = items[0];
            const detail1 = `${config.getType(first)}-${config.getSessions(first)} (${config.getStatus(first)}) ${config.dateLabel}: ${config.getDate(first)}`;
            tableRows += `<tr><td>${index + 1}</td><td>${name}</td><td>${puid}</td><td>${age}</td><td>${phone}</td><td>${center}</td><td colspan="2">${escapeHtml(detail1)}</td></tr>`;
            for (let i = 1; i < items.length; i++) {
              const item = items[i];
              const detail = `${config.getType(item)}-${config.getSessions(item)} (${config.getStatus(item)}) ${config.dateLabel}: ${config.getDate(item)}`;
              tableRows += `<tr><td></td><td></td><td></td><td></td><td></td><td></td><td colspan="2">${escapeHtml(detail)}</td></tr>`;
            }
          }
        });
      }
    } else {
      return;
    }

    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      alert("Please allow pop-ups to print.");
      return;
    }
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${escapeHtml(printTitle)}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 16px; }
            h2 { margin-bottom: 9px; }
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background: #f5f5f5; }
            p{ margin: 2px 0px; }
          </style>
        </head>
        <body>
          <h2>${escapeHtml(printTitle)}</h2>
          <p>${escapeHtml(centerOrGroupName)}</p>
          <div style="display: flex; justify-content: space-between;">
           <p>Selected Date Range  ${formatedDate(startDate) + " to " + formatedDate(endDate)}</p>
           <p>Generated on ${new Date().toLocaleString()}</p>
          </div>
          
          <table>
            <thead>${tableHeaders}</thead>
            <tbody>${tableRows}</tbody>
          </table>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.onafterprint = () => printWindow.close();
    }, 250);
  };

  const escapeHtml = (text) => {
    if (text == null) return "";
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  };
  const getAge = (dob) => {
    if (!dob) return 0;

    const birthDate = new Date(dob);
    const today = new Date();

    let age = today.getFullYear() - birthDate.getFullYear();

    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }


    return age + ' Years';

  };
  const formatedDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };
  return (
    <>
      <div className="pb-5">
        {/* <div className="d-flex justify-content-between align-items-center">
          <div>
            <Link to="/center-group-overview" className={`me-3 text-decoration-underline ${currentPath === '/center-group-overview' ? 'text-primary' : ''}`}>Group Overview</Link>
            <Link to="/" className={`text-decoration-underline ${currentPath === '/' ? 'text-primary' : ''}`}>State Overview</Link>
          </div>
          <div>
            <Link to="/center-group" className={`me-3 text-decoration-underline text-primary`}>Add Group</Link>
          </div>
        </div> */}



        {groups.length === 0 ? (
          <div className="alert alert-warning">No group found </div>
        ) :

          groups.map(group => (
            <div key={group.id} className="pt-1">

              {/* GROUP HEADER */}
              <div className="mb-10">
                <h6 className="fw-bold text-primary m-0 p-0">{group.name}</h6>
                {group.description && (
                  <div className="text-muted small">{group.description}</div>
                )}
              </div>

              {/* CENTERS */}
              <div className="row mb-3 gy-3">
                {group.centers.map(center => {
                  const widget = centerWidgets[center.hm_id];
                  const loading = centerLoading[center.hm_id];

                  return (
                    <div
                      key={center.hm_id}
                      className="col-xl-2 col-lg-3 col-md-4 col-sm-6"
                    >
                      <div
                        className="rounded-4 shadow-lg border"
                        style={{
                          background: "rgba(255,255,255,0.92)",
                          backdropFilter: "blur(10px)",
                        }}
                      >
                        {/* CENTER HEADER */}
                        <div
                          className="p-3 text-white rounded-top-4"
                          style={{
                            background:
                              "linear-gradient(135deg, #4f46e5 0%, #6366f1 50%, #818cf8 100%)",
                          }}
                        >
                          <div className="d-flex align-items-center">
                            <div
                              className="d-flex justify-content-center align-items-center rounded-circle me-2"
                              style={{
                                width: "32px",
                                height: "32px",
                                background: "rgba(255,255,255,0.2)",
                              }}
                            >
                              <MapPin size={18} />
                            </div>
                            <span className="fw-bold small">
                              {center.hm_name}
                            </span>
                          </div>
                        </div>

                        {/* LOADER */}
                        {loading && (
                          <div className="p-4 text-center">
                            <div className="spinner-border text-primary"></div>
                            <div className="small mt-2">Loading...</div>
                          </div>
                        )}

                        {/* WIDGET DATA */}
                        {!loading && widget && (
                          <div className="p-3">
                            {[
                              { title: "Leads", value: widget.total_leads, color: "#0d6efd" },
                              { title: "Appointments", value: widget.completed_appointments, color: "#198754" },
                              { title: "Patients New", value: widget.booked_package_patients, color: "#dc3545" },
                              { title: "Patients Running", value: widget.running_package_patients, color: "#6f42c1" },
                              { title: "Patients Completed", value: widget.completed_package_patients, color: "#39504d" },
                              { title: "Patients Treated", value: widget.treated_patients, color: "#e97b15" },
                            ].map((item, i) => (
                              <div
                                key={i}
                                className="d-flex justify-content-between align-items-center p-2 mb-2 rounded-3 shadow-sm"

                                style={{
                                  background: "white",
                                  cursor: "pointer",
                                  transition: "0.3s",
                                  columnGap: "10px"
                                }}
                                onClick={() => {
                                  setCenterOrGroupName(center.hm_name || "");
                                  fetchModalData(center.hm_id, item.title);
                                }}
                              >
                                <span className="small">{item.title}</span>
                                <span

                                  className="fw-bold"
                                  style={{ color: item.color }}
                                >
                                  {item.value ?? 0}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* GROUP TOTAL WIDGET */}
                {group.centers.length > 0 && (
                  <div className="col-xl-2 col-lg-3 col-md-4 col-sm-6">
                    <div
                      className="rounded-4 shadow-lg border h-100"
                      style={{
                        background: "rgba(255,255,255,0.92)",
                        backdropFilter: "blur(10px)",
                      }}
                    >
                      {/* TOTAL HEADER */}
                      <div
                        className="p-3 text-white rounded-top-4"
                        style={{
                          background:
                            "linear-gradient(135deg, #059669 0%, #10b981 50%, #34d399 100%)",
                        }}
                      >
                        <div className="d-flex align-items-center">
                          <div
                            className="d-flex justify-content-center align-items-center rounded-circle me-2"
                            style={{
                              width: "32px",
                              height: "32px",
                              background: "rgba(255,255,255,0.2)",
                            }}
                          >
                            <span className="fw-bold">∑</span>
                          </div>
                          <span className="fw-bold small">Group Total</span>
                        </div>
                      </div>

                      {/* TOTAL DATA */}
                      <div className="p-3">
                        {(() => {
                          const totals = {
                            total_leads: 0,
                            completed_appointments: 0,
                            booked_package_patients: 0,
                            running_package_patients: 0,
                            completed_package_patients: 0,
                            treated_patients: 0,
                          };

                          group.centers.forEach(center => {
                            const widget = centerWidgets[center.hm_id];
                            if (widget) {
                              totals.total_leads += widget.total_leads ?? 0;
                              totals.completed_appointments += widget.completed_appointments ?? 0;
                              totals.booked_package_patients += widget.booked_package_patients ?? 0;
                              totals.running_package_patients += widget.running_package_patients ?? 0;
                              totals.completed_package_patients += widget.completed_package_patients ?? 0;
                              totals.treated_patients += widget.treated_patients ?? 0;
                            }
                          });

                          // Collect all center IDs in this group for group-level modal calls
                          const groupCenterIds = group.centers.map(c => c.hm_id);

                          return [
                            { title: "Leads", value: totals.total_leads, color: "#0d6efd" },
                            { title: "Appointments", value: totals.completed_appointments, color: "#198754" },
                            { title: "Patients New", value: totals.booked_package_patients, color: "#dc3545" },
                            { title: "Patients Running", value: totals.running_package_patients, color: "#6f42c1" },
                            { title: "Patients Completed", value: totals.completed_package_patients, color: "#39504d" },
                            { title: "Patients Treated", value: totals.treated_patients, color: "#e97b15" },
                          ].map((item, i) => (
                            <div
                              key={i}
                              className="d-flex justify-content-between align-items-center p-2 mb-2 rounded-3 shadow-sm"
                              style={{ background: "white", cursor: "pointer" }}
                              onClick={() => {
                                setCenterOrGroupName(group.name || "");
                                fetchModalData(groupCenterIds, item.title);
                              }}
                            >
                              <span className="small fw-semibold">{item.title}</span>
                              <span
                                className="fw-bold"
                                style={{ color: item.color }}
                              >
                                {item.value}
                              </span>
                            </div>
                          ));
                        })()}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <hr />
            </div>
          ))}
      </div>

      {
        leadModel && (
          <>
            {/* Backdrop */}
            <div className="modal-backdrop fade show w-100 h-100"></div>

            <div
              className="modal d-block"
              tabIndex="-1"
              style={{ position: "fixed", overflowY: "auto" }}
            >
              <div className={`modal-dialog ${isFullScreenModal ? 'modal-fullscreen w-100' : 'modal-xl'} modal-dialog-centered modal-dialog-scrollable`}>
                <div className="modal-content">

                  {/* Header */}
                  <div className="modal-header d-flex justify-content-between align-items-center">
                    <h5 className="modal-title">Lead Details</h5>
                    <div className="d-flex align-items-center">
                      {/* download csv button */}
                      <div className=" cursor-pointer w-40-px h-40-px bg-neutral-200 rounded-circle d-flex justify-content-center align-items-center me-2"
                        onClick={printData} >
                        <i className="ri-file-pdf-2-line"></i>

                      </div>
                      <div className=" cursor-pointer w-40-px h-40-px bg-neutral-200 rounded-circle d-flex justify-content-center align-items-center me-2"
                        onClick={downloadCsv} >
                        <i className="ri-file-excel-2-line"></i>

                      </div>

                      <div className='cursor-pointer w-40-px h-40-px bg-neutral-200 rounded-circle d-flex justify-content-center align-items-center'
                        onClick={toggleFullScreenModal} >
                        {isFullScreenModal ? <i className="ri-fullscreen-exit-line"></i> : <i className="ri-fullscreen-line"></i>}

                      </div>

                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setLeadModel(false)}
                      ></button>

                    </div>

                  </div>

                  {/* Body */}
                  <div className="modal-body">
                    {/* center wise counting */}
                    {!modalLoading && modalData.length > 0 && (
                      <div className="mb-3 bg-light rounded">
                        <div className="d-flex flex-wrap gap-2">
                          {Object.entries(getCenterWiseCounts()).map(([centerName, count]) => (
                            <span key={centerName} className="badge bg-primary fs-6">
                              {centerName}: {count}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Loader */}
                    {modalLoading && (
                      <div className="p-4 text-center">
                        <div className="spinner-border text-primary"></div>
                        <p className="mt-2 mb-0 fw-bold">Loading...</p>
                      </div>
                    )}

                    {/* Table */}
                    {!modalLoading && modalData.length > 0 && (
                      <table className="table table-bordered">
                        <thead
                          style={{
                            position: "sticky",
                            top: -18,
                            backgroundColor: "white",
                            zIndex: 1,
                          }}
                        >
                          <tr>
                            <th>#</th>
                            <th>Lead Name</th>
                            <th>Phone Number</th>
                            <th>Age</th>
                            <th>Center Name</th>
                            <th>Primary Source</th>
                            <th>Status</th>
                          </tr>
                        </thead>

                        <tbody>
                          {modalData.map((lead, index) => (
                            <tr key={lead.lead_id}>
                              <td>{index + 1}</td>
                              <td>{lead.name}</td>
                              <td>{lead.phone}</td>
                              <td> {lead.dob ? getAge(lead.dob) : '-'} </td>
                              <td>{lead.center_name}</td>
                              <td>
                                {lead.source_name === "Transfer from Other Center"
                                  ? `Transferred From ${lead.transferred_from}`
                                  : lead.source_name}
                              </td>
                              <td>
                                {LeadStatusMap[lead.status] || "Unknown"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}

                    {/* Empty State */}
                    {!modalLoading && modalData.length === 0 && (
                      <p className="text-center fw-bold">No leads found</p>
                    )}

                  </div>
                </div>
              </div>
            </div>
          </>
        )
      }

      {
        appointmentModel && (
          <>
            {/* Backdrop */}
            <div className="modal-backdrop fade show w-100 h-100"></div>

            <div
              className="modal d-block"
              tabIndex="-1"
              style={{ position: "fixed", overflowY: "auto" }}
            >
              <div className={`modal-dialog ${isFullScreenModal ? 'modal-fullscreen w-100' : 'modal-xl'} modal-dialog-centered modal-dialog-scrollable`}>
                <div className="modal-content">

                  {/* Header */}
                  <div className="modal-header d-flex justify-content-between align-items-center">
                    <h5 className="modal-title">Appointments Details</h5>
                    <div className="d-flex align-items-center">
                      <div className=" cursor-pointer w-40-px h-40-px bg-neutral-200 rounded-circle d-flex justify-content-center align-items-center me-2"
                        onClick={printData} >
                        <i className="ri-file-pdf-2-line"></i>

                      </div>
                      {/* download csv button */}
                      <div className="cursor-pointer w-40-px h-40-px bg-neutral-200 rounded-circle d-flex justify-content-center align-items-center me-2"
                        onClick={downloadCsv} >
                        <i className="ri-file-excel-2-line"></i>

                      </div>

                      <div className='cursor-pointer w-40-px h-40-px bg-neutral-200 rounded-circle d-flex justify-content-center align-items-center'
                        onClick={toggleFullScreenModal} >
                        {isFullScreenModal ? <i className="ri-fullscreen-exit-line"></i> : <i className="ri-fullscreen-line"></i>}

                      </div>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setAppointmentModel(false)}
                      ></button>
                    </div>
                  </div>

                  {/* Body */}
                  <div className="modal-body">
                    {/* center wise counting */}
                    {!modalLoading && modalData.length > 0 && (
                      <div className="mb-3 bg-light rounded">
                        <div className="d-flex flex-wrap gap-2">
                          {Object.entries(getCenterWiseCounts()).map(([centerName, count]) => (
                            <span key={centerName} className="badge bg-primary fs-6">
                              {centerName}: {count}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Loader */}
                    {modalLoading && (
                      <div className="p-4 text-center">
                        <div className="spinner-border text-primary"></div>
                        <p className="mt-2 mb-0 fw-bold">Loading...</p>
                      </div>
                    )}

                    {/* Table */}
                    {!modalLoading && modalData.length > 0 && (
                      <table className="table table-bordered">
                        <thead
                          style={{
                            position: "sticky",
                            top: -18,
                            backgroundColor: "white",
                            zIndex: 1,
                          }}
                        >
                          <tr>
                            <th>#</th>
                            <th>Patient Name</th>
                            <th>Patient ID</th>
                            <th>Age</th>
                            <th>Center Name</th>
                            <th>Dr. Name</th>
                            <th>Date</th>
                          </tr>
                        </thead>

                        <tbody>
                          {modalData.map((appointment, index) => (
                            <tr key={appointment.pam_id}>
                              <td>{index + 1}</td>
                              <td>{appointment.pm_name}</td>
                              <td>{appointment.patient_unique_id}</td>
                              <td>{appointment.pm_dob ? getAge(appointment.pm_dob) : '-'}</td>
                              <td>{appointment.hm_name}</td>
                              <td>
                                {appointment.doctor_name || "N/A"}
                              </td>
                              <td>
                                {appointment.pam_app_date || "Unknown"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}

                    {/* Empty State */}
                    {!modalLoading && modalData.length === 0 && (
                      <p className="text-center fw-bold">No Appointments found</p>
                    )}

                  </div>
                </div>
              </div>
            </div>
          </>
        )
      }
      {
        patientModel && (
          <>
            {/* Backdrop */}
            <div className="modal-backdrop fade show w-100 h-100"></div>

            <div
              className="modal d-block"
              tabIndex="-1"
              style={{ position: "fixed", overflowY: "auto" }}
            >
              <div className={`modal-dialog ${isFullScreenModal ? 'modal-fullscreen w-100' : 'modal-xl'} modal-dialog-centered modal-dialog-scrollable`}>
                <div className="modal-content">

                  {/* Header */}
                  <div className="modal-header d-flex justify-content-between align-items-center">
                    <h5 className="modal-title">{patientModalHeader}</h5>
                    <div className="d-flex align-items-center">
                      <div className=" cursor-pointer w-40-px h-40-px bg-neutral-200 rounded-circle d-flex justify-content-center align-items-center me-2"
                        onClick={printData} >
                        <i className="ri-file-pdf-2-line"></i>

                      </div>
                      {/* download csv button */}
                      <div className="cursor-pointer w-40-px h-40-px bg-neutral-200 rounded-circle d-flex justify-content-center align-items-center me-2"
                        onClick={downloadCsv} >
                        <i className="ri-file-excel-2-line"></i>

                      </div>

                      <div className='cursor-pointer w-40-px h-40-px bg-neutral-200 rounded-circle d-flex justify-content-center align-items-center'
                        onClick={toggleFullScreenModal} >
                        {isFullScreenModal ? <i className="ri-fullscreen-exit-line"></i> : <i className="ri-fullscreen-line"></i>}

                      </div>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setPatientModel(false)}
                      ></button>
                    </div>
                  </div>

                  {/* Body */}
                  <div className="modal-body">
                    {/* center wise counting and package / treatment information */}
                    {!modalLoading && modalData.length > 0 && (
                      <div className="mb-3 bg-light rounded p-3">
                        {isPatientModalFromTotal ? (
                          (() => {
                            const counts = getTotalPackageCounts();
                            const patientCount = modalData.length;
                            const isPatientsTreated = dataCategory === "Patients Treated";
                            const isPatientsNew = dataCategory === "Patients New";
                            const totalTreatment = counts.totalPackageCount;
                            const sdtTreatment = counts.sdtBoosterCount + counts.sdtFullPackageCount;
                            const snbTreatment = counts.snbBoosterCount + counts.snbFullPackageCount;
                            const totalSnbBoosterSessions = counts.totalSnbBoosterSessions || 0;
                            const totalSdtBoosterSessions = counts.totalSdtBoosterSessions || 0;
                            const totalSnbSessions = counts.totalSnbSessions || 0;
                            const totalSdtSessions = counts.totalSdtSessions || 0;
                            const effectiveSnbBooster = (totalSnbBoosterSessions / 40).toFixed(2);
                            const effectiveSdtBooster = (totalSdtBoosterSessions / 20).toFixed(2);
                            const effectiveSNBPackages = (totalSnbSessions / 40).toFixed(2);
                            const effectiveSDTPackages = (totalSdtSessions / 20).toFixed(2);
                            return (
                              <>
                                <h6 className="fw-bold mb-3">Total (all centers)</h6>
                                <div className="border rounded p-3 bg-white">
                                  <div className="d-flex flex-wrap gap-2">
                                    <div className="border rounded p-2" style={{ minWidth: '140px', backgroundColor: '#f8f9fa' }}>
                                      <small className="text-muted d-block">Total Patient</small>
                                      <h6 className="mb-0 text-dark fw-bold">{patientCount}</h6>
                                    </div>
                                    {!isPatientsTreated && (
                                      <div className="border rounded p-2" style={{ minWidth: '140px', backgroundColor: '#f8f9fa' }}>
                                        <small className="text-muted d-block">Total Package</small>
                                        <h6 className="mb-0 text-dark fw-bold">{counts.totalPackageCount}</h6>
                                      </div>
                                    )}
                                    {isPatientsTreated ? (
                                      <>
                                        <div className="border rounded p-2" style={{ minWidth: '140px', backgroundColor: '#f8f9fa' }}>
                                          <small className="text-muted d-block">Total Treatment</small>
                                          <h6 className="mb-0 text-dark fw-bold">{totalTreatment}</h6>
                                        </div>
                                        <div className="border rounded p-2" style={{ minWidth: '140px', backgroundColor: '#f8f9fa' }}>
                                          <small className="text-muted d-block">SNB Treatment</small>
                                          <h6 className="mb-0 text-success fw-bold">{snbTreatment}</h6>
                                        </div>
                                        <div className="border rounded p-2" style={{ minWidth: '140px', backgroundColor: '#f8f9fa' }}>
                                          <small className="text-muted d-block">SDT Treatment</small>
                                          <h6 className="mb-0 text-success fw-bold">{sdtTreatment}</h6>
                                        </div>
                                      </>
                                    ) : (
                                      <>
                                        <div className="border rounded p-2" style={{ minWidth: '140px', backgroundColor: '#f8f9fa' }}>
                                          <small className="text-muted d-block">SDT Booster</small>
                                          <h6 className="mb-0 text-danger fw-bold">{counts.sdtBoosterCount}</h6>
                                        </div>
                                        <div className="border rounded p-2" style={{ minWidth: '140px', backgroundColor: '#f8f9fa' }}>
                                          <small className="text-muted d-block">SDT Full Package</small>
                                          <h6 className="mb-0 text-success fw-bold">{counts.sdtFullPackageCount}</h6>
                                        </div>
                                        <div className="border rounded p-2" style={{ minWidth: '140px', backgroundColor: '#f8f9fa' }}>
                                          <small className="text-muted d-block">SNB Booster</small>
                                          <h6 className="mb-0 text-danger fw-bold">{counts.snbBoosterCount}</h6>
                                        </div>
                                        <div className="border rounded p-2" style={{ minWidth: '140px', backgroundColor: '#f8f9fa' }}>
                                          <small className="text-muted d-block">SNB Full Package</small>
                                          <h6 className="mb-0 text-success fw-bold">{counts.snbFullPackageCount}</h6>
                                        </div>
                                        {isPatientsNew && (
                                          <>
                                            <div className="border rounded p-2" style={{ minWidth: '170px', backgroundColor: '#f8f9fa' }}>
                                              <small className="text-muted d-block">Total SNB Booster Sessions</small>
                                              <h6 className="mb-0 text-dark fw-bold">{totalSnbBoosterSessions}</h6>
                                            </div>
                                            <div className="border rounded p-2" style={{ minWidth: '170px', backgroundColor: '#f8f9fa' }}>
                                              <small className="text-muted d-block">Total SDT Booster Sessions</small>
                                              <h6 className="mb-0 text-dark fw-bold">{totalSdtBoosterSessions}</h6>
                                            </div>
                                            <div className="border rounded p-2" style={{ minWidth: '170px', backgroundColor: '#f8f9fa' }}>
                                              <small className="text-muted d-block">Effective SNB Booster Package</small>
                                              <h6 className="mb-0 text-primary fw-bold">{effectiveSnbBooster}</h6>
                                            </div>
                                            <div className="border rounded p-2" style={{ minWidth: '170px', backgroundColor: '#f8f9fa' }}>
                                              <small className="text-muted d-block">Effective SDT Booster Package</small>
                                              <h6 className="mb-0 text-primary fw-bold">{effectiveSdtBooster}</h6>
                                            </div>
                                            <div className="border rounded p-2" style={{ minWidth: '170px', backgroundColor: '#f8f9fa' }}>
                                              <small className="text-muted d-block">Effective SNB Packages</small>
                                              <h6 className="mb-0 text-primary fw-bold">{effectiveSNBPackages}</h6>
                                            </div>
                                            <div className="border rounded p-2" style={{ minWidth: '170px', backgroundColor: '#f8f9fa' }}>
                                              <small className="text-muted d-block">Effective SDT Packages</small>
                                              <h6 className="mb-0 text-primary fw-bold">{effectiveSDTPackages}</h6>
                                            </div>
                                          </>
                                        )}
                                      </>
                                    )}
                                  </div>
                                </div>
                              </>
                            );
                          })()
                        ) : (
                          <>
                            <h6 className="fw-bold mb-3">Package Information by Center:</h6>
                            {Object.entries(getPackageCountsByCenter()).map(([centerName, counts]) => {
                              const patientCount = getCenterWiseCounts()[centerName] || 0;
                              const isPatientsTreated = dataCategory === "Patients Treated";
                              const isPatientsNew = dataCategory === "Patients New";

                              // For Patients Treated, derive treatment-specific aggregates
                              const totalTreatment = counts.totalPackageCount;
                              const sdtTreatment = counts.sdtBoosterCount + counts.sdtFullPackageCount;
                              const snbTreatment = counts.snbBoosterCount + counts.snbFullPackageCount;

                              // For Patients New, compute effective booster packages
                              const totalSnbBoosterSessions = counts.totalSnbBoosterSessions || 0;
                              const totalSdtBoosterSessions = counts.totalSdtBoosterSessions || 0;
                              const totalSnbSessions = counts.totalSnbSessions || 0;
                              const totalSdtSessions = counts.totalSdtSessions || 0;
                              const effectiveSnbBooster =
                                (totalSnbBoosterSessions / 40).toFixed(2);
                              const effectiveSdtBooster =
                                (totalSdtBoosterSessions / 20).toFixed(2);
                              const effectiveSNBPackages = (totalSnbSessions / 40).toFixed(2);
                              const effectiveSDTPackages = (totalSdtSessions / 20).toFixed(2);
                              return (
                                <div key={centerName} className="mb-3 border rounded p-3 bg-white">
                                  <h6 className="fw-bold text-primary mb-2">{centerName}</h6>
                                  <div className="d-flex flex-wrap gap-2">
                                    <div className="border rounded p-2" style={{ minWidth: '140px', backgroundColor: '#f8f9fa' }}>
                                      <small className="text-muted d-block">Total Patient</small>
                                      <h6 className="mb-0 text-dark fw-bold">{patientCount}</h6>
                                    </div>
                                    {!isPatientsTreated && (
                                      <div className="border rounded p-2" style={{ minWidth: '140px', backgroundColor: '#f8f9fa' }}>
                                        <small className="text-muted d-block">Total Package</small>
                                        <h6 className="mb-0 text-dark fw-bold">{counts.totalPackageCount}</h6>
                                      </div>
                                    )}

                                    {/* For Patients Treated, show only treatment boxes */}
                                    {isPatientsTreated ? (
                                      <>
                                        <div className="border rounded p-2" style={{ minWidth: '140px', backgroundColor: '#f8f9fa' }}>
                                          <small className="text-muted d-block">Total Treatment</small>
                                          <h6 className="mb-0 text-dark fw-bold">{totalTreatment}</h6>
                                        </div>
                                        <div className="border rounded p-2" style={{ minWidth: '140px', backgroundColor: '#f8f9fa' }}>
                                          <small className="text-muted d-block">SNB Treatment</small>
                                          <h6 className="mb-0 text-success fw-bold">{snbTreatment}</h6>
                                        </div>
                                        <div className="border rounded p-2" style={{ minWidth: '140px', backgroundColor: '#f8f9fa' }}>
                                          <small className="text-muted d-block">SDT Treatment</small>
                                          <h6 className="mb-0 text-success fw-bold">{sdtTreatment}</h6>
                                        </div>
                                      </>
                                    ) : (
                                      <>
                                        <div className="border rounded p-2" style={{ minWidth: '140px', backgroundColor: '#f8f9fa' }}>
                                          <small className="text-muted d-block">SDT Booster</small>
                                          <h6 className="mb-0 text-danger fw-bold">{counts.sdtBoosterCount}</h6>
                                        </div>
                                        <div className="border rounded p-2" style={{ minWidth: '140px', backgroundColor: '#f8f9fa' }}>
                                          <small className="text-muted d-block">SDT Full Package</small>
                                          <h6 className="mb-0 text-success fw-bold">{counts.sdtFullPackageCount}</h6>
                                        </div>
                                        <div className="border rounded p-2" style={{ minWidth: '140px', backgroundColor: '#f8f9fa' }}>
                                          <small className="text-muted d-block">SNB Booster</small>
                                          <h6 className="mb-0 text-danger fw-bold">{counts.snbBoosterCount}</h6>
                                        </div>
                                        <div className="border rounded p-2" style={{ minWidth: '140px', backgroundColor: '#f8f9fa' }}>
                                          <small className="text-muted d-block">SNB Full Package</small>
                                          <h6 className="mb-0 text-success fw-bold">{counts.snbFullPackageCount}</h6>
                                        </div>

                                        {isPatientsNew && (
                                          <>
                                            <div className="border rounded p-2" style={{ minWidth: '170px', backgroundColor: '#f8f9fa' }}>
                                              <small className="text-muted d-block">Total SNB Booster Sessions</small>
                                              <h6 className="mb-0 text-dark fw-bold">{totalSnbBoosterSessions}</h6>
                                            </div>
                                            <div className="border rounded p-2" style={{ minWidth: '170px', backgroundColor: '#f8f9fa' }}>
                                              <small className="text-muted d-block">Total SDT Booster Sessions</small>
                                              <h6 className="mb-0 text-dark fw-bold">{totalSdtBoosterSessions}</h6>
                                            </div>
                                            <div className="border rounded p-2" style={{ minWidth: '170px', backgroundColor: '#f8f9fa' }}>
                                              <small className="text-muted d-block">Effective SNB Booster Package</small>
                                              <h6 className="mb-0 text-primary fw-bold">{effectiveSnbBooster}</h6>
                                            </div>
                                            <div className="border rounded p-2" style={{ minWidth: '170px', backgroundColor: '#f8f9fa' }}>
                                              <small className="text-muted d-block">Effective SDT Booster Package</small>
                                              <h6 className="mb-0 text-primary fw-bold">{effectiveSdtBooster}</h6>
                                            </div>
                                            <div className="border rounded p-2" style={{ minWidth: '170px', backgroundColor: '#f8f9fa' }}>
                                              <small className="text-muted d-block">Effective SNB Packages</small>
                                              <h6 className="mb-0 text-primary fw-bold">{effectiveSNBPackages}</h6>
                                            </div>
                                            <div className="border rounded p-2" style={{ minWidth: '170px', backgroundColor: '#f8f9fa' }}>
                                              <small className="text-muted d-block">Effective SDT Packages</small>
                                              <h6 className="mb-0 text-primary fw-bold">{effectiveSDTPackages}</h6>
                                            </div>
                                          </>
                                        )}
                                      </>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </>
                        )}
                      </div>
                    )}

                    {/* Loader */}
                    {modalLoading && (
                      <div className="p-4 text-center">
                        <div className="spinner-border text-primary"></div>
                        <p className="mt-2 mb-0 fw-bold">Loading...</p>
                      </div>
                    )}

                    {/* Table */}
                    {!modalLoading && modalData.length > 0 && (

                      <table className="table table-bordered">
                        <thead
                          style={{
                            position: "sticky",
                            top: -18,
                            backgroundColor: "white",
                            zIndex: 1,
                          }}
                        >
                          <tr>
                            <th>#</th>
                            <th>Patient Name</th>
                            <th>PUID</th>
                            <th>Age</th>
                            <th>Phone</th>
                            <th>Center Name</th>

                            {dataCategory === "Patients Treated" ? (
                              <>
                                <th className="text-center">SNB Treatments</th>
                                <th className="text-center">SDT Treatments</th>
                              </>
                            ) : ('')}
                            <th colSpan={2}>{config.label}</th>
                          </tr>
                        </thead>

                        <tbody>
                          {modalData.map((patient, index) => {
                            const items = patient[config.key] || [];
                            const visibleItems = items.slice(0, 2);
                            const isExpanded = expandedRows[patient.pm_id];

                            return (
                              <React.Fragment key={patient.pm_id}>
                                {/* MAIN ROW */}
                                <tr>
                                  <td>{index + 1}</td>
                                  <td>{patient.patient_name}</td>
                                  <td>{patient.patient_unique_id ?? "N/A"}</td>
                                  <td>{patient.pm_dob ? getAge(patient.pm_dob) : '-'}</td>
                                  <td>{patient.phone ?? "N/A"}</td>
                                  <td>{patient.center_name}</td>
                                  {dataCategory === "Patients Treated" ? (
                                    <>
                                      <td className="text-center">{patient.treatment_count_snb ?? 0}</td>
                                      <td className="text-center">{patient.treatment_count_sdt ?? 0}</td>
                                    </>
                                  ) : ('')}

                                  <td colSpan={2}>
                                    {visibleItems.map((item, i) => (
                                      <DetailItem
                                        key={i}
                                        type={config.getType(item)}
                                        sessions={config.getSessions(item)}
                                        status={config.getStatus(item)}
                                        dateLabel={config.dateLabel}
                                        date={config.getDate(item)}
                                        last_treatment_date={config.getLastTreatmentDate(item)}
                                      />
                                    ))}

                                    {items.length > 2 && (
                                      <div
                                        className="text-primary small fw-semibold cursor-pointer mt-1"
                                        onClick={() => toggleRow(patient.pm_id)}
                                      >
                                        {isExpanded
                                          ? "▲ Hide details"
                                          : `▼ View all (${items.length})`}
                                      </div>
                                    )}
                                  </td>
                                </tr>

                                {/* COLLAPSED ROW */}
                                {isExpanded && (
                                  <tr className="bg-light">
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <td></td>

                                    <td colSpan={2}>
                                      <div style={{ maxHeight: 200, overflowY: "auto" }}>
                                        {items.map((item, i) => (
                                          <DetailItem
                                            key={i}
                                            type={config.getType(item)}
                                            sessions={config.getSessions(item)}
                                            status={config.getStatus(item)}
                                            dateLabel={config.dateLabel}
                                            date={config.getDate(item)}
                                            last_treatment_date={config.getLastTreatmentDate(item)}
                                          />
                                        ))}
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}

                        </tbody>
                      </table>
                    )}

                    {/* Empty State */}
                    {!modalLoading && modalData.length === 0 && (
                      <p className="text-center fw-bold">No Details found</p>
                    )}

                  </div>
                </div>
              </div>
            </div>
          </>
        )
      }
    </>
  );
};

export default GroupOverview;
